import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import meddLogo from '@/assets/medd-logo-white.png';
import meddIcon from '@/assets/medd-icon-white.png';
import {
  Home,
  Activity,
  TrendingUp,
  Users,
  UserCircle,
  BarChart,
  ChevronLeft,
  ChevronRight,
  LogOut,
  LayoutDashboard,
  UserCog,
  ClipboardList,
  Building2,
  Trophy,
  ExternalLink,
  Eye,
  BookOpen,
  Gamepad2,
  Settings2,
  GraduationCap,
  HelpCircle,
  Award,
  MessageSquare,
  Sparkles,
  Mail,
  History,
  Radio,
  Network,
  Wifi,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCpdEnabled, useTenant, PoweredBy } from '@/contexts/TenantContext';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useOrganisation } from '@/contexts/OrganisationContext';

interface SidebarProps {
  allowCollapse?: boolean;
  className?: string;
}

export function Sidebar({ allowCollapse = true, className }: SidebarProps) {
  const { hasRole, logout, user } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const cpdEnabled = useCpdEnabled();
  const { canManagePeople, hasEnterpriseHierarchy } = useOrganisation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Check if user is HCP (Healthcare Professional) - HCPs don't see gamification/leaderboards
  const isHcp = user?.is_hcp ?? (user?.user_type ? user.user_type === 'hcp' : true); // Default to HCP if not set
  
  // Tenant logo handling - use tenant logo if available
  // White-label tenants should NEVER show MEDD logo
  const hasTenantLogo = tenant.logo_dark_url || tenant.logo_url;
  const logoUrl = hasTenantLogo ? (tenant.logo_url || tenant.logo_dark_url) : (isMasterTenant ? meddLogo : null);
  const iconUrl = hasTenantLogo ? (tenant.logo_dark_url || tenant.logo_url) : (isMasterTenant ? meddIcon : null);
  const tenantName = tenant.tenant_name || (isMasterTenant ? 'MEDD-SIM' : 'Training Platform');

  const baseNavItems = [
    { to: '/', label: 'Dashboard', icon: Home },
    { to: '/simulations', label: 'My Simulations', icon: Activity },
    { to: '/my-sessions', label: 'My Sessions', icon: ClipboardList },
    { to: '/coaching/history', label: 'Coaching History', icon: UserCircle },
    { to: '/learning/pathways', label: 'Learning Pathways', icon: GraduationCap },
    { to: '/progress', label: 'Progress', icon: TrendingUp },
    { to: '/roundtable', label: 'Roundtable', icon: Radio },
    { to: '/whats-new', label: "What's New", icon: Sparkles },
    ...(!isMasterTenant ? [{ to: '/connection-check', label: 'Connection Check', icon: Wifi }] : []),
    // Only show Leaderboards for non-HCP users
    ...(!isHcp ? [{ to: '/leaderboards', label: 'Leaderboards', icon: Trophy }] : []),
  ];

  const adminNavItems = [
    { to: '/admin', label: 'Admin Dashboard', icon: LayoutDashboard },
    { to: '/admin/users', label: 'User Management', icon: UserCog },
    { to: '/admin/activity', label: 'Activity & Logs', icon: History },
    ...(hasEnterpriseHierarchy && canManagePeople
      ? [{ to: '/admin/organisation', label: 'Organisation', icon: Network }]
      : []),
    { to: '/admin/email-history', label: 'Email History', icon: Mail },
    { to: '/admin/agents', label: 'Agents', icon: Users },
    { to: '/admin/agent-templates', label: 'Agent Templates', icon: Sparkles },
    { to: '/admin/coaching-templates', label: 'Output Templates', icon: MessageSquare },
    { to: '/admin/pathways', label: 'Pathway Management', icon: GraduationCap },
    { to: '/content-studio', label: 'Content Studio', icon: Sparkles },
    { to: '/admin/certificates', label: 'Certificates', icon: Award },
    { to: '/admin/rubric-templates', label: 'Rubric Templates', icon: ClipboardList },
    { to: '/admin/assessments', label: 'Physical Assessments', icon: ClipboardList },
    // Enterprise tenants manage branches and units from the Organisation workspace.
    ...(hasEnterpriseHierarchy
      ? []
      : isMasterTenant
        ? [{ to: '/admin/companies', label: 'Companies', icon: Building2 }]
        : [{ to: '/admin/companies', label: 'Branches', icon: Building2 }]),
    // Only show these for master tenant (MEDD) - hide from white-label tenants
    ...(isMasterTenant ? [{ to: '/admin/tenants', label: 'White-Label Tenants', icon: Building2 }] : []),
    // Only show Gamification for non-HCP users
    ...(!isHcp && cpdEnabled ? [{ to: '/company-dashboard/gamification', label: 'Gamification', icon: Gamepad2 }] : []),
    // Only show system config and debugger for master tenant
    ...(isMasterTenant ? [{ to: '/admin/system-config', label: 'System Configuration', icon: Settings2 }] : []),
    ...(isMasterTenant ? [{ to: '/admin/user-view-debugger', label: 'User View Debugger', icon: Eye }] : []),
  ];

  const companyAdminNavItems = [
    {
      to: hasEnterpriseHierarchy ? '/admin' : '/company-dashboard',
      label: hasEnterpriseHierarchy ? 'Organisation Dashboard' : 'Company Dashboard',
      icon: LayoutDashboard,
    },
    ...(hasEnterpriseHierarchy && canManagePeople
      ? [{ to: '/admin/organisation', label: 'Organisation', icon: Network }]
      : []),
    { to: '/admin/agents', label: 'Agents', icon: Users },
    { to: '/admin/agent-templates', label: 'Agent Templates', icon: Sparkles },
    { to: '/admin/coaching-templates', label: 'Output Templates', icon: MessageSquare },
    { to: '/admin/pathways', label: 'Pathway Management', icon: GraduationCap },
    { to: '/content-studio', label: 'Content Studio', icon: Sparkles },
    { to: '/admin/certificates', label: 'Certificates', icon: Award },
    { to: '/team-performance', label: 'Team Performance', icon: BarChart },
    // Only show Gamification for non-HCP users
    ...(!isHcp && cpdEnabled ? [{ to: '/company-dashboard/gamification', label: 'Gamification', icon: Gamepad2 }] : []),
  ];

  const scopedManagerNavItems = [
    { to: '/admin', label: 'Team Dashboard', icon: LayoutDashboard },
    { to: '/admin/organisation', label: 'Organisation', icon: Network },
    { to: '/admin/agents', label: 'Agents', icon: Users },
    { to: '/admin/agent-templates', label: 'Agent Templates', icon: Sparkles },
    { to: '/admin/coaching-templates', label: 'Output Templates', icon: MessageSquare },
    { to: '/admin/pathways', label: 'Pathway Management', icon: GraduationCap },
    { to: '/content-studio', label: 'Content Studio', icon: Sparkles },
    { to: '/admin/certificates', label: 'Certificates', icon: Award },
    { to: '/admin/rubric-templates', label: 'Rubric Templates', icon: ClipboardList },
    { to: '/team-performance', label: 'Team Performance', icon: BarChart },
  ];

  const helpNavItems = [
    { to: '/help/knowledge-base', label: 'Knowledge Base', icon: HelpCircle },
  ];

  return (
    <aside
      className={cn(
        'h-screen sticky top-0 transition-all duration-300 flex flex-col',
        'bg-primary',
        isCollapsed ? 'w-20' : 'w-64',
        className
      )}
    >
      {/* Logo Header with Collapse Toggle */}
      <div className="px-4 py-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          {isCollapsed ? (
            <div className="flex justify-center w-full">
              {isMasterTenant && iconUrl ? (
                <img 
                  src={iconUrl} 
                  alt={`${tenantName} Logo`} 
                  className="h-8 w-8 object-contain"
                  onError={(e) => {
                    // Hide on error for white-label
                    if (!isMasterTenant) {
                      (e.target as HTMLImageElement).style.display = 'none';
                    } else {
                      (e.target as HTMLImageElement).src = meddIcon;
                    }
                  }}
                />
              ) : (
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {(tenant.tenant_name || 'T').substring(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex min-w-0 flex-col items-start gap-1">
                {logoUrl ? (
                  <div className={cn(
                    'flex h-9 w-36 items-center justify-center overflow-hidden rounded px-2',
                    isMasterTenant ? 'bg-transparent' : 'bg-white',
                  )}>
                    <img
                      src={logoUrl}
                      alt={`${tenantName} Logo`}
                      className={cn(
                        'h-full w-full object-contain',
                        !isMasterTenant && 'scale-[1.65]',
                      )}
                      onError={(e) => {
                        if (isMasterTenant) {
                          (e.target as HTMLImageElement).src = meddLogo;
                        } else {
                          const image = e.target as HTMLImageElement;
                          image.style.display = 'none';
                          image.nextElementSibling?.classList.remove('hidden');
                        }
                      }}
                    />
                    <span className={cn('hidden truncate text-sm font-semibold text-primary')}>
                      {tenantName}
                    </span>
                  </div>
                ) : (
                  // White-label without logo - show tenant name as text
                  <span className="text-white font-bold text-lg">
                    {tenantName}
                  </span>
                )}
                <div className="text-white/50 text-[10px] pl-0.5">
                  {isMasterTenant ? 'Simulation Platform' : tenantName}
                </div>
              </div>
              
              {/* Collapse Toggle - Top Right */}
              {allowCollapse && (
                <button
                  onClick={() => setIsCollapsed(true)}
                  className="p-1.5 hover:bg-white/10 rounded-md transition-colors"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeft className="w-4 h-4 text-white/60" />
                </button>
              )}
            </>
          )}
        </div>
        
        {/* When collapsed, show expand button centered */}
        {allowCollapse && isCollapsed && (
          <button
            onClick={() => setIsCollapsed(false)}
            className="mt-3 w-full flex justify-center p-1.5 hover:bg-white/10 rounded-md transition-colors"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="w-4 h-4 text-white/60" />
          </button>
        )}
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto">
        {/* Base Navigation */}
        {baseNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200',
                'text-white/80 hover:text-white hover:bg-white/10',
                isActive && 'bg-white/10 text-white border-l-4 border-success shadow-lg'
              )
            }
          >
            <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
            {!isCollapsed && (
              <span className="text-[13px] font-medium">{item.label}</span>
            )}
          </NavLink>
        ))}

        {/* Admin Section */}
        {hasRole('admin') && (
          <>
            {!isCollapsed && (
              <div className="px-3 pt-5 pb-1.5 text-[10px] font-semibold text-white/50 uppercase tracking-wider">
                Administration
              </div>
            )}
            {isCollapsed && <div className="h-px bg-white/20 my-3 mx-2" />}
            {adminNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200',
                    'text-white/80 hover:text-white hover:bg-white/10',
                    isActive && 'bg-white/10 text-white border-l-4 border-success shadow-lg'
                  )
                }
              >
                <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                {!isCollapsed && (
                  <span className="text-[13px] font-medium flex items-center gap-2">
                    {item.label}
                    {item.to === '/content-studio' && (
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-white/30 text-white/70">
                        Beta
                      </Badge>
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </>
        )}

        {/* Company Admin Section */}
        {hasRole('company_admin') && (
          <>
            {!isCollapsed && (
              <div className="px-3 pt-5 pb-1.5 text-[10px] font-semibold text-white/50 uppercase tracking-wider">
                Company Management
              </div>
            )}
            {isCollapsed && <div className="h-px bg-white/20 my-3 mx-2" />}
            {companyAdminNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200',
                    'text-white/80 hover:text-white hover:bg-white/10',
                    isActive && 'bg-white/10 text-white border-l-4 border-success shadow-lg'
                  )
                }
              >
                <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                {!isCollapsed && (
                  <span className="text-[13px] font-medium flex items-center gap-2">
                    {item.label}
                    {item.to === '/content-studio' && (
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-white/30 text-white/70">
                        Beta
                      </Badge>
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </>
        )}

        {canManagePeople && !hasRole('admin') && !hasRole('company_admin') && (
          <>
            {!isCollapsed && (
              <div className="px-3 pt-5 pb-1.5 text-[10px] font-semibold text-white/50 uppercase tracking-wider">
                Team Management
              </div>
            )}
            {isCollapsed && <div className="h-px bg-white/20 my-3 mx-2" />}
            {scopedManagerNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200',
                    'text-white/80 hover:text-white hover:bg-white/10',
                    isActive && 'bg-white/10 text-white border-l-4 border-success shadow-lg'
                  )
                }
              >
                <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                {!isCollapsed && <span className="text-[13px] font-medium">{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}

        {/* Help Section */}
        {!isCollapsed && (
          <div className="px-3 pt-5 pb-1.5 text-[10px] font-semibold text-white/50 uppercase tracking-wider">
            Help & Support
          </div>
        )}
        {isCollapsed && <div className="h-px bg-white/20 my-3 mx-2" />}
        {helpNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200',
                'text-white/80 hover:text-white hover:bg-white/10',
                isActive && 'bg-white/10 text-white border-l-4 border-success shadow-lg'
              )
            }
          >
            <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
            {!isCollapsed && <span className="text-[13px] font-medium">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-white/10 p-3 space-y-2">
        {/* Return to MEDD - Only show for master tenant */}
        {isMasterTenant && (
          <a
            href="https://medd.com.au/news-feed/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 text-white"
          >
            <ExternalLink className="w-4 h-4" />
            {!isCollapsed && <span className="text-[13px] font-medium">Return to MEDD</span>}
          </a>
        )}

        {/* Logout */}
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-destructive/90 hover:bg-destructive rounded-lg transition-all duration-200 text-white"
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && <span className="text-[13px] font-medium">Logout</span>}
        </button>
        
        {/* Powered by MEDD-SIM - Show for white-label tenants */}
        {!isMasterTenant && tenant.powered_by_visible && !isCollapsed && (
          <div className="pt-2 text-center">
            <a 
              href={tenant.powered_by_url || 'https://medd.com.au'} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[10px] text-white/40 hover:text-white/60 transition-colors"
            >
              {tenant.powered_by_text || 'Powered by MEDD-SIM'}
            </a>
          </div>
        )}
      </div>
    </aside>
  );
}
